import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import TokenIcon from "@mui/icons-material/Token";
import RefreshIcon from "@mui/icons-material/Refresh";
import LoginIcon from "@mui/icons-material/Login";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import { useSnackbar } from "notistack";
import { useJumboDialog } from "@jumbo/components/JumboDialog/hooks/useJumboDialog";
import { generateToken, getCustomers } from "app/services/api/management";
import { getServicePartnerCustomers } from "app/services/api/servicePartners";
import { loginAsCustomer } from "app/services/loginAsCustomer";
import { useDsokCustomerDetail } from "../data/useDsokCustomerDetail";
import {
  getActiveSectionFromPathname,
  setAtPath,
  normalizeSectionPayload,
} from "../data/dsokCustomerDetailSectionConfig";
import { setImpersonationOrigin } from "app/utils/impersonationOrigin";
import { updateDsokCustomerSettings } from "../data/dsokCustomerDetailApi";
import { sanitizeLogoUrlString } from "app/pages/customers/dsok/utils/sanitizeLogoUrlString";
import DsokCustomerDetailMenu from "./DsokCustomerDetailMenu";

const USER_TYP_ADMIN = 1;
const USER_TYP_PARTNER = 2;
const USER_TYP_SERVICE_PARTNER = 7;

const DsokCustomerDetailLayoutContent = ({ lastBreadcrumbLabel, children }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const {
    customerUrl,
    data: customer,
    refetch,
    isDirty,
    originalDsokSettings,
    setOriginalDsokSettings,
    getSectionUpdatePayload,
    resetDirty,
  } = useDsokCustomerDetail();
  const { t } = useTranslation();
  const { showDialog, hideDialog } = useJumboDialog();
  const { enqueueSnackbar } = useSnackbar();
  const [generatingToken, setGeneratingToken] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [loginInProgress, setLoginInProgress] = useState(false);
  const isSectionSubpage =
    customerUrl && pathname.startsWith(`/dsok/customer-detail/${customerUrl}/`);
  const auth = useJumboAuth();
  const authUser = auth?.getAuthUser?.() || {};
  const userTyp = authUser?.type ?? authUser?.typ;
  const customerType =
    authUser?.customer?.customerType ?? authUser?.customer?.customer_type;
  const effectiveTyp =
    Number(customerType) === USER_TYP_SERVICE_PARTNER
      ? USER_TYP_SERVICE_PARTNER
      : Number(userTyp);
  const isAdmin = effectiveTyp === USER_TYP_ADMIN;
  const isAdminForActions = effectiveTyp === USER_TYP_ADMIN;
  const canLoginAsCustomer =
    effectiveTyp === USER_TYP_ADMIN ||
    effectiveTyp === USER_TYP_PARTNER ||
    effectiveTyp === USER_TYP_SERVICE_PARTNER;

  const lastCrumb =
    customer?.id != null && customer?.companyName != null
      ? `[${customer.id}] ${customer.companyName}`
      : customer?.companyName || customer?.company_name || "";

  const handleGenerateToken = async () => {
    if (generatingToken || customer?.id == null) return;
    setGeneratingToken(true);
    try {
      const token = await generateToken(customer.id);
      showDialog({
        variant: "default",
        title: t("dsokCustomerDetail.tokenModal.title"),
        content: (
          <Box
            display="flex"
            flexDirection="column"
            gap={1}
            alignItems="flex-end"
          >
            <TextField
              fullWidth
              name="token"
              value={token}
              multiline
              rows={3}
              margin="dense"
              sx={{ mt: 0 }}
              InputProps={{
                readOnly: true,
              }}
            />
            <Button
              variant="contained"
              size="small"
              sx={{
                boxShadow: "none",
                height: "38px",
                width: "fit-content",
              }}
              startIcon={<ContentCopyIcon />}
              onClick={() => {
                navigator.clipboard.writeText(token);
                enqueueSnackbar(
                  t("dsokCustomerDetail.tokenModal.tokenCopied"),
                  {
                    variant: "success",
                  },
                );
                hideDialog();
              }}
            >
              {t("buttons.copy")}
            </Button>
          </Box>
        ),
      });
    } catch (error) {
      enqueueSnackbar(t("dsokCustomerDetail.tokenModal.generateFailed"), {
        variant: "error",
      });
    } finally {
      setGeneratingToken(false);
    }
  };
  const handleUpdate = () => {
    if (!isDirty || updating) return;
    const activeSection = getActiveSectionFromPathname(pathname);
    if (!activeSection) {
      enqueueSnackbar(t("dsokCustomerDetail.updateConfirm.error"), {
        variant: "warning",
      });
      return;
    }
    let payload = getSectionUpdatePayload(activeSection.sectionId);
    if (payload == null && activeSection.sectionId !== "ui-settings") {
      const validationMessage =
        activeSection.sectionId === "devices"
          ? t("messages.checkRequiredFields")
          : t("dsokCustomerDetail.updateConfirm.error");
      enqueueSnackbar(validationMessage, {
        variant: "warning",
      });
      return;
    }
    if (activeSection.sectionId === "ui-settings" && payload == null) {
      payload = {};
    }
    showDialog({
      variant: "default",
      title: t("dsokCustomerDetail.updateConfirm.title"),
      content: (
        <Typography>{t("dsokCustomerDetail.updateConfirm.message")}</Typography>
      ),
      actions: (
        <>
          <Button
            variant="contained"
            onClick={() => {
              hideDialog();
              runSectionUpdate(activeSection, payload);
            }}
          >
            {t("dsokCustomerDetail.updateConfirm.yes")}
          </Button>
          <Button onClick={hideDialog}>
            {t("dsokCustomerDetail.updateConfirm.no")}
          </Button>
        </>
      ),
    });
  };

  const runSectionUpdate = async (activeSection, payload) => {
    if (!customerUrl || !originalDsokSettings) return;
    if (activeSection.sectionId === "app-settings" && payload?.loyaltySystem?.isEnabled === true && payload?.loyaltySystem?.type === "vexion") {
      const businessId = (payload?.loyaltySystem?.vexion?.businessId ?? "").trim();
      if (!businessId) {
        enqueueSnackbar(t("pages.dsokDetails.appSettings.tabs.loyalty.businessIdRequired"), {
          variant: "error",
        });
        return;
      }
    }
    setUpdating(true);
    try {
      let payloadToNormalize = payload;
      let customerMainLogoFromPayload;
      let customerAuthFromPayload;
      let customerSecondaryAuthFromPayload;
      let customerPaymentMethodsFromPayload;
      let dsokPosFromPayload;
      let dsokProductDetailFromPayload;
      let orderNotificationsFromPayload;

      if (activeSection.sectionId === "app-settings" && payload != null && typeof payload === "object") {
        payloadToNormalize = JSON.parse(JSON.stringify(payload));
        customerMainLogoFromPayload = payloadToNormalize._customerMainLogo;
        customerAuthFromPayload = payloadToNormalize._customerAuth;
        customerSecondaryAuthFromPayload = payloadToNormalize._customerSecondaryAuth;
        customerPaymentMethodsFromPayload = payloadToNormalize._customerPaymentMethods;
        delete payloadToNormalize._customerMainLogo;
        delete payloadToNormalize._customerAuth;
        delete payloadToNormalize._customerSecondaryAuth;
        delete payloadToNormalize._customerPaymentMethods;
      }
      if (activeSection.sectionId === "settings" && payload != null && typeof payload === "object") {
        payloadToNormalize = JSON.parse(JSON.stringify(payload));
        dsokPosFromPayload = payloadToNormalize._dsokPos;
        dsokProductDetailFromPayload = payloadToNormalize._dsokProductDetail;
        // Notifikácie objednávok patria do settings.orderNotifications, teda mimo
        // cesty tejto sekcie (["customer"]). Rovnaký vzor ako _dsokPos vyššie.
        orderNotificationsFromPayload = payloadToNormalize._orderNotifications;
        delete payloadToNormalize._dsokPos;
        delete payloadToNormalize._dsokProductDetail;
        delete payloadToNormalize._orderNotifications;
      }

      const normalized =
        typeof payloadToNormalize === "object" && payloadToNormalize !== null
          ? normalizeSectionPayload(
              activeSection.sectionId,
              typeof payloadToNormalize === "object" && payloadToNormalize !== null
                ? JSON.parse(JSON.stringify(payloadToNormalize))
                : payloadToNormalize,
            )
          : payloadToNormalize;
      const base = JSON.parse(JSON.stringify(originalDsokSettings));
      let newDsokSettings = setAtPath(base, activeSection.path, normalized);

      if (activeSection.sectionId === "app-settings") {
        const hasCustomerOverrides =
          customerMainLogoFromPayload !== undefined ||
          customerAuthFromPayload !== undefined ||
          customerSecondaryAuthFromPayload !== undefined ||
          customerPaymentMethodsFromPayload !== undefined;
        if (hasCustomerOverrides) {
          newDsokSettings = {
            ...newDsokSettings,
            customer: {
              ...(newDsokSettings.customer || {}),
              ...(customerMainLogoFromPayload !== undefined && {
                mainLogo: (() => {
                  const merged =
                    customerMainLogoFromPayload ??
                    newDsokSettings.customer?.mainLogo;
                  if (merged == null || typeof merged !== "string") {
                    return merged;
                  }
                  const cleaned = sanitizeLogoUrlString(merged);
                  if (
                    process.env.NODE_ENV === "development" &&
                    cleaned !== merged
                  ) {
                    console.info(
                      "[DsokCustomerDetail] mainLogo normalized before save",
                      { inLen: merged.length, outLen: cleaned.length },
                    );
                  }
                  return cleaned;
                })(),
              }),
              ...(customerAuthFromPayload !== undefined && {
                auth: customerAuthFromPayload ?? newDsokSettings.customer?.auth,
              }),
              ...(customerSecondaryAuthFromPayload !== undefined && {
                secondaryAuth: customerSecondaryAuthFromPayload ?? newDsokSettings.customer?.secondaryAuth,
              }),
              ...(customerPaymentMethodsFromPayload !== undefined && {
                paymentMethods: customerPaymentMethodsFromPayload ?? newDsokSettings.customer?.paymentMethods,
              }),
            },
          };
        }
      }
      if (activeSection.sectionId === "settings") {
        const hasDsokSettingsOverrides =
          dsokPosFromPayload !== undefined || dsokProductDetailFromPayload !== undefined;
        if (hasDsokSettingsOverrides) {
          newDsokSettings = {
            ...newDsokSettings,
            ...(dsokPosFromPayload !== undefined && {
              pos: {
                ...(newDsokSettings.pos || {}),
                ...(dsokPosFromPayload || {}),
              },
            }),
            ...(dsokProductDetailFromPayload !== undefined && {
              productDetail: {
                ...(newDsokSettings.productDetail || {}),
                ...(dsokProductDetailFromPayload || {}),
              },
            }),
          };
        }
        if (orderNotificationsFromPayload !== undefined) {
          newDsokSettings = {
            ...newDsokSettings,
            settings: {
              ...(newDsokSettings.settings || {}),
              orderNotifications: orderNotificationsFromPayload,
            },
          };
        }
      }

      if (process.env.NODE_ENV === "development" && activeSection.sectionId === "app-settings") {
        console.info("[DsokCustomerDetail] app-settings payload (customer slice):", {
          mainLogo: newDsokSettings.customer?.mainLogo != null ? "(set)" : undefined,
          auth: newDsokSettings.customer?.auth,
          secondaryAuth: newDsokSettings.customer?.secondaryAuth,
          paymentMethods: newDsokSettings.customer?.paymentMethods,
        });
      }
      await updateDsokCustomerSettings(customerUrl, newDsokSettings);
      setOriginalDsokSettings(newDsokSettings);
      resetDirty();
      await refetch?.();
      if (process.env.NODE_ENV === "development") {
        console.info(
          "[DsokCustomerDetail] update success:",
          activeSection.sectionId,
        );
      }
      enqueueSnackbar(t("dsokCustomerDetail.updateConfirm.success"), {
        variant: "success",
      });
    } catch (error) {
      const status = error?.response?.status;
      const data = error?.response?.data;
      if (process.env.NODE_ENV === "development") {
        console.warn(
          "[DsokCustomerDetail] update failed:",
          activeSection.sectionId,
          status,
          data,
          "→ UI: failure",
        );
      }
      const isRealFailure =
        status == null || status >= 400 || (data && data.success === false);
      if (isRealFailure) {
        const message =
          data?.message ??
          data?.error ??
          t("dsokCustomerDetail.updateConfirm.error");
        enqueueSnackbar(message, { variant: "error" });
      } else {
        enqueueSnackbar(t("dsokCustomerDetail.updateConfirm.success"), {
          variant: "success",
        });
        let payloadForFallbackNormalize = payload;
        let dsokPosFromFallback;
        let dsokProductDetailFromFallback;
        if (activeSection.sectionId === "settings" && payload != null && typeof payload === "object") {
          payloadForFallbackNormalize = JSON.parse(JSON.stringify(payload));
          dsokPosFromFallback = payloadForFallbackNormalize._dsokPos;
          dsokProductDetailFromFallback =
            payloadForFallbackNormalize._dsokProductDetail;
          delete payloadForFallbackNormalize._dsokPos;
          delete payloadForFallbackNormalize._dsokProductDetail;
        }
        const normalized =
          typeof payloadForFallbackNormalize === "object" &&
          payloadForFallbackNormalize !== null
            ? normalizeSectionPayload(
                activeSection.sectionId,
                JSON.parse(JSON.stringify(payloadForFallbackNormalize)),
              )
            : payloadForFallbackNormalize;
        const base = JSON.parse(JSON.stringify(originalDsokSettings));
        let nextDsokSettings = setAtPath(base, activeSection.path, normalized);
        if (activeSection.sectionId === "settings") {
          nextDsokSettings = {
            ...nextDsokSettings,
            ...(dsokPosFromFallback !== undefined && {
              pos: {
                ...(nextDsokSettings.pos || {}),
                ...(dsokPosFromFallback || {}),
              },
            }),
            ...(dsokProductDetailFromFallback !== undefined && {
              productDetail: {
                ...(nextDsokSettings.productDetail || {}),
                ...(dsokProductDetailFromFallback || {}),
              },
            }),
          };
        }
        setOriginalDsokSettings(nextDsokSettings);
        resetDirty();
        refetch?.();
      }
    } finally {
      setUpdating(false);
    }
  };

  const handleLogin = async () => {
    if (loginInProgress || !customerUrl) return;
    const authUser = auth?.getAuthUser?.() ?? {};
    const customerId =
      authUser?.customer?.id ?? authUser?.customerId ?? authUser?.id;
    if (!customerId) {
      enqueueSnackbar(
        t("notifications.wasFailed", { defaultValue: "Failed" }),
        { variant: "error" },
      );
      return;
    }
    setLoginInProgress(true);
    try {
      const isServicePartner = effectiveTyp === USER_TYP_SERVICE_PARTNER;
      const list = isServicePartner
        ? await getServicePartnerCustomers(customerId)
        : await getCustomers(customerId);
      const items = Array.isArray(list) ? list : [];
      const item = items.find(
        (c) =>
          c.customerData?.url === customerUrl ||
          c.customerData?.id === customer?.id ||
          c.url === customerUrl ||
          c.id === customer?.id,
      );
      const customerWithUsers = item
        ? { ...(item.customerData || item), users: item.users ?? [] }
        : null;
      if (!customerWithUsers?.users?.length) {
        enqueueSnackbar(
          t("notifications.wasFailed", { defaultValue: "Failed" }),
          { variant: "error" },
        );
        return;
      }
      setImpersonationOrigin({
        originRoute: pathname,
        originCustomerHash: customerUrl,
        originUserId: authUser?.userId ?? authUser?.id,
        originUserType: effectiveTyp,
        originTab: getActiveSectionFromPathname(pathname)?.sectionId ?? undefined,
      });
      await loginAsCustomer(customerWithUsers, {
        setAuthToken: auth?.setAuthToken,
        navigate,
        enqueueSnackbar,
        t,
      });
    } catch {
      enqueueSnackbar(
        t("notifications.wasFailed", { defaultValue: "Failed" }),
        { variant: "error" },
      );
    } finally {
      setLoginInProgress(false);
    }
  };

  const generateTokenLabel = t("dsokCustomerDetail.actions.generateToken");
  const updateLabel = t("dsokCustomerDetail.actions.update");
  const updateDisabledLabel = t("dsokCustomerDetail.actions.noChangesToSave");
  const loginLabel = t("dsokCustomerDetail.actions.login");

  return (
    <Box sx={{ px: 0 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: 2,
        }}
      >
        <Breadcrumbs
          aria-label="breadcrumb"
          sx={{ flex: "1 1 auto", minWidth: 0 }}
        >
          <Link to="/">{t("sidebar.menuItem.home")}</Link>
          {isAdmin && (
            <Link to="/dsok/customers">{t("sidebar.menuItem.customers")}</Link>
          )}
          {lastCrumb &&
            (lastBreadcrumbLabel ? (
              <Link
                to={`/dsok/customer-detail/${customerUrl}`}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                {lastCrumb}
              </Link>
            ) : (
              <Typography color="text.primary">{lastCrumb}</Typography>
            ))}
          {lastBreadcrumbLabel && (
            <Typography color="text.primary">{lastBreadcrumbLabel}</Typography>
          )}
        </Breadcrumbs>
        <Stack
          direction="row"
          spacing={1}
          sx={{ flexShrink: 0, flexWrap: "wrap" }}
        >
          {isAdminForActions && (
            <Tooltip title={generateTokenLabel}>
              <span>
                <Button
                  color="primary"
                  size="small"
                  variant="outlined"
                  onClick={handleGenerateToken}
                  aria-label={generateTokenLabel}
                  disabled={generatingToken || customer?.id == null}
                  startIcon={
                    generatingToken ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <TokenIcon fontSize="small" />
                    )
                  }
                >
                  {generateTokenLabel}
                </Button>
              </span>
            </Tooltip>
          )}
          <Tooltip title={isDirty ? updateLabel : updateDisabledLabel}>
            <span>
              <Button
                color="primary"
                size="small"
                variant="outlined"
                onClick={handleUpdate}
                aria-label={updateLabel}
                disabled={!isDirty || updating}
                startIcon={
                  updating ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <RefreshIcon fontSize="small" />
                  )
                }
                sx={{
                  ...(!isDirty && {
                    opacity: 0.5,
                    cursor: "not-allowed",
                    "&:hover": { backgroundColor: "transparent" },
                  }),
                }}
              >
                {updateLabel}
              </Button>
            </span>
          </Tooltip>
          {canLoginAsCustomer && (
            <Tooltip title={loginLabel}>
              <span>
                <Button
                  color="primary"
                  size="small"
                  variant="outlined"
                  onClick={handleLogin}
                  aria-label={loginLabel}
                  disabled={loginInProgress}
                  startIcon={
                    loginInProgress ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <LoginIcon fontSize="small" />
                    )
                  }
                >
                  {loginLabel}
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>
      </Box>
      {isSectionSubpage && <DsokCustomerDetailMenu />}
      {children}
    </Box>
  );
};

const DsokCustomerDetailLayout = ({ lastBreadcrumbLabel, children }) => (
  <DsokCustomerDetailLayoutContent lastBreadcrumbLabel={lastBreadcrumbLabel}>
    {children}
  </DsokCustomerDetailLayoutContent>
);

export default DsokCustomerDetailLayout;
